import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect } from 'react';
import { imgOt7GoldenTicketHeroWebp, imgOt7LogoBlackSvg } from '../../images';
import { colorFromInt } from '../../lib';
import { Urls } from '../../lib/enums/urls';
import styles from '../../lib/styles.json';
import { Ot7BracketGame } from '../../models/Ot7BracketGame';
import { Ot7Game } from '../../models/Ot7Game';
import './GoldenTicketBracket.scss';

type TeamParams = {
	seed: number;
	logo: string;
	name: string;
	score: number;
	status: 'Winner' | 'Loser' | 'Pending';
	backgroundColor: string;
};

type GameParams = {
	name: string;
	homeTeam: TeamParams;
	awayTeam: TeamParams;
	bracketSide?: 'Top' | 'Bottom';
};

type GameColumnParams = {
	games: GameParams[];
	size: number;
	bracketSide?: 'Left' | 'Right' | 'Center';
};

const getColumnInfo = ({ gameColumns }: { gameColumns: GameParams[][] }): GameColumnParams[] => {
	const middleIndex = Math.floor(gameColumns.length / 2);
	const isOddLength = gameColumns.length % 2 !== 0;

	return gameColumns.map((column, index) => {
		let bracketSide: 'Left' | 'Center' | 'Right';

		if (isOddLength && index === middleIndex) {
			bracketSide = 'Center';
		} else if (index < middleIndex) {
			bracketSide = 'Left';
		} else {
			bracketSide = 'Right';
		}

		return {
			games: column,
			size: column.length,
			bracketSide: bracketSide,
		};
	});
};

const _Team = ({ team }: { team: TeamParams }) => {
	return team ? (
		<div className={`Team ${team.status}`}>
			{/* <div className="Seed">{team.seed}</div> */}
			<div className="Logo" style={{ backgroundColor: team.backgroundColor }}>
				<img src={team.logo} />
			</div>
			<div className="TeamName">{team.name}</div>
			<div className="Score">{team.score}</div>
		</div>
	) : (
		<div className="Team">
			{/* <div className="Seed">{team.seed}</div> */}
			<div className="Logo" style={{ backgroundColor: '#ffffff', padding: '4px', aspectRatio: '1/1' }}>
				<img src={imgOt7LogoBlackSvg} />
			</div>
			<div className="TeamName">TBD</div>
			<div className="Score"></div>
		</div>
	);
};

const _Game = ({ game }: { game: GameParams }) => {
	return (
		<div className="GameContainer">
			<div className={`GameDetails`}>
				<div className="Title">{game.name}</div>
				<div className="Teams">
					<_Team team={game.awayTeam} />
					<div className="Divider"></div>
					<_Team team={game.homeTeam} />
				</div>
			</div>
		</div>
	);
};

const GoldenTicketBracket = () => {
	const [bracketGames, setBracketGames] = React.useState<Ot7BracketGame[]>([]);
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);

	useEffect(() => {
		const fetchGames = async () => {
			setLoading(true);
			setError(null);

			try {
				const response = await fetch(`${Urls.RestApiBaseUrl}/ot7_www/ot7_bracket_games/v1/public`);
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				const data = await response.json();

				setBracketGames(data.ot7_bracket_games);
			} catch (e) {
				setError('An unknown error occurred');
				console.error('Failed to fetch games:', e);
			} finally {
				setLoading(false);
			}
		};

		fetchGames();
	}, []);

	const ot7GameToGameParams = ({ name, ot7_game }: { name: string; ot7_game: Ot7Game }): GameParams => {
		const homeTeam = ot7_game?.ot7_games_ot7_teams?.find((team) => team.is_home_team)?.ot7_team;
		const homeTeamScore = ot7_game?.ot7_games_ot7_teams?.find((team) => team.is_home_team)?.points;
		const awayTeam = ot7_game?.ot7_games_ot7_teams?.find((team) => !team.is_home_team)?.ot7_team;
		const awayTeamScore = ot7_game?.ot7_games_ot7_teams?.find((team) => !team.is_home_team)?.points;
		const homeStatus = ot7_game?.status != 'confirmed' ? 'Pending' : homeTeamScore > awayTeamScore ? 'Winner' : 'Loser';
		const awayStatus = ot7_game?.status != 'confirmed' ? 'Pending' : awayTeamScore > homeTeamScore ? 'Winner' : 'Loser';

		return {
			name: name,
			homeTeam: homeTeam
				? {
						seed: null,
						logo: `${Urls.OvertimeImagesBaseUrl}/${homeTeam.logo_path}`,
						name: homeTeam.name,
						score: homeTeamScore,
						status: homeStatus,
						backgroundColor: colorFromInt(homeTeam.primary_color),
					}
				: null,
			awayTeam: awayTeam
				? {
						seed: null,
						logo: `${Urls.OvertimeImagesBaseUrl}/${awayTeam.logo_path}`,
						name: awayTeam.name,
						score: awayTeamScore,
						status: awayStatus,
						backgroundColor: colorFromInt(awayTeam.primary_color),
					}
				: null,
		};
	};

	const findAndMapGame = (
		allBracketGames: Ot7BracketGame[],
		bracketGameName: string,
		displayName: string,
	): GameParams => {
		const foundBracketGame = allBracketGames.find((g) => g.name === bracketGameName);
		const ot7Game = foundBracketGame?.ot7_game;
		return ot7GameToGameParams({ name: displayName, ot7_game: ot7Game });
	};

	const playInAParams: GameParams = findAndMapGame(bracketGames, 'PlayInA', 'Play-In');
	const playInBParams: GameParams = findAndMapGame(bracketGames, 'PlayInB', 'Play-In');
	const quarterfinalsAParams: GameParams = findAndMapGame(bracketGames, 'QuarterfinalsA', 'Quarterfinals');
	const quarterfinalsBParams: GameParams = findAndMapGame(bracketGames, 'QuarterfinalsB', 'Quarterfinals');
	const quarterfinalsCParams: GameParams = findAndMapGame(bracketGames, 'QuarterfinalsC', 'Quarterfinals');
	const quarterfinalsDParams: GameParams = findAndMapGame(bracketGames, 'QuarterfinalsD', 'Quarterfinals');
	const semifinalsAParams: GameParams = findAndMapGame(bracketGames, 'SemifinalsA', 'Semifinals');
	const semifinalsBParams: GameParams = findAndMapGame(bracketGames, 'SemifinalsB', 'Semifinals');
	const championshipParams: GameParams = findAndMapGame(bracketGames, 'Championship', 'Championship');

	// columns for the bracket
	const playInGameA = [playInAParams];
	const quarterFinalGamesA = [quarterfinalsAParams, quarterfinalsBParams];
	const semiFinalGamesA = [semifinalsAParams];
	const finalGame = [championshipParams];
	const semiFinalGamesB = [semifinalsBParams];
	const quarterFinalGamesB = [quarterfinalsCParams, quarterfinalsDParams];
	const playInGameB = [playInBParams];

	// split into 2 different groups so that play in doesn't have to mix into the rest of the brackets
	const playInGameColumns = [playInGameA, [], [], [], playInGameB];
	const gameColumns = [quarterFinalGamesA, semiFinalGamesA, finalGame, semiFinalGamesB, quarterFinalGamesB];

	// add column info to add styles for connecting lines
	const playInGameColumnsInfo = getColumnInfo({ gameColumns: playInGameColumns });
	const gameColumnsInfo = getColumnInfo({ gameColumns: gameColumns });

	const tables = [playInGameColumnsInfo, gameColumnsInfo];

	const isFinalComplete =
		championshipParams?.awayTeam?.status === 'Winner' || championshipParams?.homeTeam?.status === 'Winner';
	const finalChampion =
		isFinalComplete && championshipParams?.awayTeam?.status === 'Winner'
			? championshipParams?.awayTeam?.name
			: isFinalComplete && championshipParams?.homeTeam?.status === 'Winner'
				? championshipParams?.homeTeam?.name
				: null;

	if (!bracketGames.length) {
		return (
			<div className="GoldenTicketBracket">
				<div className="BracketContainer">
					<div className="Loading">Loading...</div>
				</div>
			</div>
		);
	}

	return (
		<div className="GoldenTicketBracket">
			<div className={`BracketContainer Container-Size-${gameColumnsInfo.length}`}>
				<div className="HeroImage">
					<img src={imgOt7GoldenTicketHeroWebp} />
				</div>

				{finalChampion && (
					<div className="ChampionTeam">
						<div className="Line"></div>
						<div className="Title">Champion</div>
						<div className="Winner">
							<FontAwesomeIcon icon={faTrophy} color={styles.COLOR.BRAND_PRIMARY} size="2x" />
							{finalChampion}
						</div>
					</div>
				)}

				{tables.map((table, index) => (
					<React.Fragment key={`game-table-${index}`}>
						<React.Fragment key={`game-table-${index}`}>
							{table.map((column, index) => (
								<div
									className={`BracketColumn Size-${column.size} ${index == 0 ? 'First' : index == table.length - 1 ? 'Last' : ''}`}
									key={`game-column-${index}`}
								>
									{column.games.map((game, gameIndex) => (
										<div className={`Game ${column.bracketSide}-Side`} key={`game-${index}-${gameIndex}`}>
											<div className="Lines-Before"></div>
											<div className="Line"></div>
											<_Game game={game} />
											<div className="Line"></div>
											<div className="Lines-After"></div>
										</div>
									))}
								</div>
							))}
						</React.Fragment>
						{index == 0 && (
							<React.Fragment key={`game-table-vertical-lines-${index}`}>
								{/* <div className={`BracketColumn Size-5} ${index == 0 ? 'First' : index == table.length - 1 ? 'Last' : ''}`}></div> */}
								<div className={`BracketColumn First`}>
									<div className="VerticalLine First"></div>
								</div>
								<div className={`BracketColumn`}></div>
								<div className={`BracketColumn`}></div>
								<div className={`BracketColumn`}></div>
								<div className={`BracketColumn Last`}>
									<div className="VerticalLine Last"></div>
								</div>
							</React.Fragment>
						)}
					</React.Fragment>
				))}
			</div>
		</div>
	);
};

export default GoldenTicketBracket;
